import { View, Text } from "@src/components";
import { memo, useMemo } from "react";
import { Pressable, ViewProps } from "react-native";
import { StyleSheet } from "react-native-unistyles";

export type ListItem = {
  id: string;
  name: string;
  email?: string;
  phoneNumber?: string;
  itemStyle?: ViewProps["style"];
};

type ListProps = {
  items: ListItem[];
  onItemPress?: (itemId: string) => void;
  renderRight?: (item: ListItem) => React.ReactNode;
  containerStyle?: ViewProps["style"];
  itemStyle?: ViewProps["style"];
};

const ListRow = memo(
  ({
    item,
    isLast,
    onItemPress,
    renderRight,
    itemStyle,
  }: {
    item: ListItem;
    isLast: boolean;
    onItemPress?: (itemId: string) => void;
    renderRight?: (item: ListItem) => React.ReactNode;
    itemStyle?: ViewProps["style"];
  }) => {
    const isLastItemStyle = useMemo(() => ({ borderBottomWidth: isLast ? 0 : 1 }), [isLast]);

    const content = (
      <View
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        style={[styles.item, isLastItemStyle, itemStyle]}
      >
        <View gap="xs" flex={1}>
          <Text textStyle="sm/medium" numberOfLines={2}>
            {item.name || item.email}
          </Text>
          {item.email && item.name && (
            <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1}>
              {item.email}
            </Text>
          )}
          {item.phoneNumber && (
            <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1}>
              {item.phoneNumber}
            </Text>
          )}
        </View>
        {renderRight && (
          <View flexDirection="row" alignItems="center">
            {renderRight(item)}
          </View>
        )}
      </View>
    );

    if (onItemPress) {
      return <Pressable onPress={() => onItemPress(item.id)}>{content}</Pressable>;
    }

    return content;
  },
);

export const List = memo(({ items, onItemPress, renderRight, containerStyle, itemStyle }: ListProps) => {
  return (
    <View style={[styles.container, containerStyle]}>
      {items.map((item, index) => (
        <ListRow
          key={item.id}
          item={item}
          isLast={index === items.length - 1}
          onItemPress={onItemPress}
          renderRight={renderRight}
          itemStyle={itemStyle}
        />
      ))}
    </View>
  );
});

const styles = StyleSheet.create((theme) => ({
  container: {
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    overflow: "hidden",
    paddingLeft: theme.spacing.xl,
    paddingRight: theme.spacing.xs,
    backgroundColor: theme.colors.surfaceCard,
  },
  item: {
    paddingVertical: theme.spacing["3xl"],
    borderBottomColor: theme.colors.borderCard,
    gap: theme.spacing.xxs,
    paddingRight: theme.spacing.md,
    backgroundColor: theme.colors.surfaceCard,
  },
}));
